/**
 * One floor's Medium-tier parts, built with the floor at both tiers (the tier
 * only decides what is drawn, so `setTier` never rebuilds geometry): the
 * exploration mask, the merged rock mass, the lighting plan, its fixtures and
 * baked volume (`lighting.ts`) and the zone map (the rock mass and the
 * exploration mask are built by the world first: the set dressing seats on
 * the rock, and both tiers draw fog from the mask). The light volume's voxels are
 * computed on the first Medium frame (`prepare`), so an Off floor does not pay
 * for them. `release` frees the GPU copies when the view goes to Off;
 * `dispose` frees everything with the floor.
 */
import type { Theme } from '@ab/content-schema';
import type { Floor, Unit } from '@ab/game-core';
import * as THREE from 'three';
import type { ExplorationMask } from './fog.ts';
import { tileIndex } from './grid.ts';
import { type FloorLighting, buildLighting } from './lighting.ts';
import { type LightingPlan, planLighting } from './lighting-plan.ts';
import type { PropParts } from './props.ts';
import type { RockMass } from './rock-mass.ts';
import { bakeZoneMap } from './zones.ts';

export type MediumFloor = {
  readonly mask: ExplorationMask;
  readonly rock: THREE.InstancedMesh;
  readonly lighting: FloorLighting;
  readonly plan: LightingPlan;
  readonly zoneMap: THREE.DataTexture;
  readonly zoneRect: readonly [number, number, number, number];
  /** Fills the light volume if it is not yet (the first Medium frame). */
  prepare(): void;
  updateFog(seen: ReadonlySet<number>): void;
  release(): void;
  dispose(): void;
};

export const buildMediumFloor = (floor: Floor, units: readonly Unit[], seed: number, theme: Theme, mass: RockMass, mask: ExplorationMask, props: PropParts, rockMaterial: THREE.Material): MediumFloor => {
  const rock = new THREE.InstancedMesh(mass.geometry, rockMaterial, 1);
  rock.name = 'medium-rock-mass';
  rock.setMatrixAt(0, new THREE.Matrix4());
  rock.setColorAt(0, new THREE.Color(1, 1, 1));
  rock.frustumCulled = false;
  rock.castShadow = true;
  rock.receiveShadow = true;
  const heights = new Map<number, number>();
  const wallHeight = (x: number, y: number): number => {
    const tile = tileIndex(floor, x, y);
    let h = heights.get(tile);
    if (h === undefined) {
      const top = mass.topAt(x, -y);
      // A stub south of open ground is no face for a sconce.
      h = top === null || top < 1 ? 0 : top;
      heights.set(tile, h);
    }
    return h;
  };
  const plan = planLighting({ floor, units, seed, theme, tracks: props.tracks, frames: props.frames, obstacles: props.obstacles, wallHeight, topAt: mass.topAt });
  const lighting = buildLighting(floor, theme, plan, mask, seed);
  const zones = bakeZoneMap(floor, plan, theme);
  return {
    mask,
    rock,
    lighting,
    plan,
    zoneMap: zones.texture,
    zoneRect: zones.rect,
    prepare() {
      lighting.bake.fill();
    },
    updateFog(seen) {
      mask.update(seen);
      lighting.updateFog(seen);
    },
    release() {
      // The exploration mask stays: the Off tier's smooth fog samples it too.
      mass.geometry.dispose();
      rock.dispose();
      zones.texture.dispose();
      lighting.release();
    },
    dispose() {
      rock.removeFromParent();
      rock.dispose();
      mass.geometry.dispose();
      mask.texture.dispose();
      zones.texture.dispose();
      lighting.dispose();
    },
  };
};
